import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError, uploadFile, fileUrl } from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_KEPSEK } from "@/lib/nav";
import { toast } from "sonner";
import { Plus, FileText, Trash2, Upload, X } from "lucide-react";
import { FilterBar } from "@/components/FilterBar";

const F_EMPTY = { siswa: "", guru: "" };

export default function JadwalBelajar() {
  const [items, setItems] = useState([]);
  const [siswa, setSiswa] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ siswa_id: "", file_id: null });
  const [uploading, setUploading] = useState(false);
  const [filters, setFilters] = useState(F_EMPTY);
  const [applied, setApplied] = useState(F_EMPTY);

  const load = useCallback(async () => {
    const [{ data }, { data: s }] = await Promise.all([api.get("/jadwal-belajar"), api.get("/siswa", { params: { status: "aktif" } })]);
    setItems(data); setSiswa(s);
  }, []);
  useEffect(() => { load(); }, [load]);

  const assigned = new Set(items.map(i => i.siswa_id));
  const siswaAvailable = siswa.filter(s => !assigned.has(s.id));

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try { const res = await uploadFile(f); setForm({ ...form, file_id: res.id }); toast.success("File diunggah"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.file_id) return toast.error("Upload jadwal dahulu");
    try { await api.post("/jadwal-belajar", form); toast.success("Tersimpan"); setShow(false); setForm({ siswa_id: "", file_id: null }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Hapus jadwal?")) return; await api.delete(`/jadwal-belajar/${id}`); toast.success("Dihapus"); load(); };

  return (
    <Layout nav={NAV_KEPSEK}>
      <PageTitle title="Jadwal Belajar" action={
        <button onClick={() => setShow(true)} className="pp-btn-primary" data-testid="open-jadwal"><Plus className="w-4 h-4" />Input Jadwal Belajar</button>
      } />

      <FilterBar
        filters={filters} setFilters={setFilters}
        onApply={() => setApplied({ ...filters })}
        onReset={() => { setFilters(F_EMPTY); setApplied(F_EMPTY); }}
        dataTestidPrefix="jadwal-filter"
        fields={[
          { key: "siswa", type: "text", label: "Nama Siswa", placeholder: "Cari nama siswa" },
          { key: "guru", type: "text", label: "Nama Guru", placeholder: "Cari nama guru" },
        ]}
      />

      {show && (
        <section className="pp-card p-6 mb-6 animate-fade-up">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-semibold">Input Jadwal Belajar</h3>
            <button onClick={() => setShow(false)}><X className="w-5 h-5 text-slate-400" /></button>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div><label className="pp-label">Siswa *</label>
              <select required className="pp-input" value={form.siswa_id} onChange={(e) => setForm({...form, siswa_id: e.target.value})}>
                <option value="">Pilih siswa</option>{siswaAvailable.map(s => <option key={s.id} value={s.id}>{s.nama}</option>)}
              </select></div>
            <div><label className="pp-label">Upload Jadwal Belajar (PDF) *</label>
              <label className="pp-btn-secondary cursor-pointer"><Upload className="w-4 h-4" />{uploading ? "Mengunggah…" : (form.file_id ? "Ganti File" : "Pilih File PDF")}
                <input type="file" accept=".pdf" onChange={upload} className="hidden" /></label>
              {form.file_id && <span className="ml-3 text-sm text-emerald-600">✓ File siap</span>}
            </div>
            <div className="flex justify-end gap-2"><button type="button" onClick={() => setShow(false)} className="pp-btn-secondary">Batal</button><button type="submit" className="pp-btn-primary">Simpan</button></div>
          </form>
        </section>
      )}

      <section className="pp-card pp-freeze overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="pp-table-header">
            <th className="text-left px-5 py-3">Nama Siswa</th>
            <th className="text-left px-5 py-3">Guru Pendamping</th>
            <th className="text-left px-5 py-3">Jadwal</th>
            <th className="text-right px-5 py-3">Aksi</th>
          </tr></thead>
          <tbody>
            {items.filter((it) => {
              if (applied.siswa && !(it.nama_siswa || "").toLowerCase().includes(applied.siswa.toLowerCase())) return false;
              if (applied.guru && !(it.nama_guru || "").toLowerCase().includes(applied.guru.toLowerCase())) return false;
              return true;
            }).length === 0 && <tr><td colSpan={4} className="text-center py-10 text-slate-400 italic">Belum ada jadwal.</td></tr>}
            {items.filter((it) => {
              if (applied.siswa && !(it.nama_siswa || "").toLowerCase().includes(applied.siswa.toLowerCase())) return false;
              if (applied.guru && !(it.nama_guru || "").toLowerCase().includes(applied.guru.toLowerCase())) return false;
              return true;
            }).map((it) => (
              <tr key={it.id} className="pp-row">
                <td className="px-5 py-3 font-medium">{it.nama_siswa}</td>
                <td className="px-5 py-3 text-slate-600">{it.nama_guru}</td>
                <td className="px-5 py-3"><a href={fileUrl(it.file_id)} target="_blank" rel="noreferrer" className="text-indigo-600 inline-flex items-center gap-1 text-xs"><FileText className="w-3.5 h-3.5" />Lihat Jadwal</a></td>
                <td className="px-5 py-3 text-right"><button onClick={() => del(it.id)} className="pp-btn-danger"><Trash2 className="w-3.5 h-3.5" />Hapus</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </Layout>
  );
}
